import axios from 'axios';
import {generatePersonalizedOpener} from './openerGenerator';
import { scrapeLinkedInProfile, isValidLinkedInUrl, extractLinkedInUsername, processLinkedInDataForOpeners } from './linkedinScraper';
import { scrapeTalkingPoints, isValidWebsiteForTalkingPoints, processTalkingPointsForOpeners } from './talkingPointsScraper';
import { qualifyProspect, shouldQualifyProspect } from './prospectQualifier';
import serviceWorkerManager from './serviceWorkerManager';
import pageVisibilityManager from './pageVisibilityManager';
import { backgroundTaskService } from './backgroundTaskService.js';

// Normalize URL to ensure it has a protocol
export const normalizeUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `https://${url}`;
};

// Extract domain from URL
export const extractDomain = (url) => {
  try {
    const hostname = new URL(normalizeUrl(url)).hostname;
    return hostname.replace(/^www\./, '');
  } catch (e) {
    return url;
  }
};

// Format company name for display
export const formatCompanyName = (company, url) => {
  if (company && typeof company === 'string' && company.trim()) {
    return company.trim();
  }
  // Fall back to domain name
  return extractDomain(url);
};

// Validate URL format
const isValidUrl = (url) => {
  try {
    const urlObj = new URL(normalizeUrl(url));
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

// Scrape website using the external API
export const scrapeWebsite = async (url) => {
  try {
    // Validate URL before processing
    if (!url || typeof url !== 'string' || url.trim() === '') {
      throw new Error('Invalid URL: URL is empty or not a string');
    }

    // Ensure URL has protocol
    const fullUrl = normalizeUrl(url.trim());
    
    // Validate the normalized URL
    if (!isValidUrl(fullUrl)) {
      throw new Error(`Invalid URL format: ${fullUrl}`);
    }

    console.log(`Scraping website content for: ${fullUrl}`);

    // Use the external API for scraping with proper headers and method
    const response = await axios({
      method: 'post',
      url: 'https://wa.pressupwards.com/scrape',
      data: {
        url: fullUrl,
        take_screenshot: false // Always false - screenshots disabled
      },
      headers: {
        'Content-Type': 'application/json'
        // Note: User-Agent header removed as browsers don't allow setting it manually
      },
      timeout: 30000 // 30 second timeout
    });

    // Check if the API request was successful
    if (response.status === 200 && response.data) {
      const data = response.data;
      
      // Extract content from the response
      const title = data.title || '';
      const content = data.content || '';
      const wordCount = data.word_count || 0;
      
      // Extract links
      const internalLinks = data.links?.internal || [];
      const externalLinks = data.links?.external || [];
      
      // Extract media information
      const images = data.media?.images || [];
      const videos = data.media?.videos || [];
      
      // Format headings (if available)
      const headings = data.headings ? data.headings.join('. ') : '';
      
      // Format navigation (from internal links)
      const navigationLinks = internalLinks.slice(0, 15).join(',');

      return {
        title,
        metaDescription: data.meta_description || '',
        keywords: data.keywords || '',
        headings,
        navigation: navigationLinks,
        content,
        wordCount,
        internalLinks,
        externalLinks,
        images,
        videos,
        url: fullUrl,
        source: 'pressupwards-api',
        timestamp: new Date().toISOString()
      };
    } else {
      throw new Error('Failed to fetch website data from API');
    }
  } catch (error) {
    // Enhanced error logging with specific status codes
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;
      console.error(`Scraping API failed with status ${status} (${statusText}):`, {
        url: url,
        status,
        statusText,
        data: error.response.data
      });
      
      // Handle specific error codes
      if (status === 422) {
        console.warn('422 Error: The API rejected the request. This may be due to an invalid URL format or the website being inaccessible.');
      } else if (status === 429) {
        console.warn('429 Error: Rate limit exceeded. Please wait before making more requests.');
      } else if (status >= 500) {
        console.warn('Server Error: The scraping service is experiencing issues.');
      }
    } else if (error.request) {
      console.error('Network error - no response received:', error.message);
    } else {
      console.error('Scraping API failed:', error.message);
    }
    
    // Fallback to domain-based information
    const domain = extractDomain(url);
    return {
      title: `${domain.charAt(0).toUpperCase() + domain.slice(1)} - Website`,
      metaDescription: `${domain} is a website focused on providing services and information.`,
      headings: `Welcome to ${domain}. Our Services. About Us. Contact Information.`,
      content: `This is a website for ${domain}. Based on the domain name, it appears to be related to ${domain.split('.')[0]}. The website likely contains information about their products or services, contact details, and company information.`,
      url: normalizeUrl(url),
      source: 'fallback',
      timestamp: new Date().toISOString(),
      error: error.response ? `API Error ${error.response.status}` : 'Network Error'
    };
  }
};

// AI analysis functions removed - now using raw website data directly

// Main analysis function
export const analyzeWebsites = async (websites, config, onProgress, serviceOptions = {}, scrapingOptions = { scrapeWebsite: true, scrapeLinkedIn: false, scrapeTalkingPoints: false }) => {
  const results = [];
  
  // Log settings for opener generation
  console.log(`Using model: ${config.model} with endpoint: ${config.endpoint} for opener generation${config.useNoThink ? ' and /nothink suffix' : ''}`);
  
  // Check if config is valid based on endpoint type
  const configValid = 
    ((config.endpoint === 'openai' || config.endpoint === 'openrouter' || config.endpoint === 'custom') && config.apiKey && config.model) || 
    (config.endpoint === 'ollama' && config.ollamaEndpoint && config.model);
  
  if (!configValid) {
    onProgress(websites.length);
    const errorMessage = 
      !config.model ? 'Model name is required for opener generation. Please configure API settings.' : 
      config.endpoint === 'ollama' && !config.ollamaEndpoint ? 'Ollama endpoint URL is required for opener generation.' : 
      'API key is required for opener generation.';
    
    return websites.map(website => ({
      ...website,
      url: normalizeUrl(website.url),
      type: 'website_analysis',
      summary: errorMessage,
      personalizedOpener: '[Configuration error: ' + errorMessage + ']',
      error: 'Missing configuration',
      serviceType: serviceOptions.serviceType || 'website',
      subServiceType: serviceOptions.subServiceType || null
    }));
  }

  for (let i = 0; i < websites.length; i++) {
    const website = websites[i];
    onProgress(i);
    
    try {
      // Ensure the website has a valid URL
      const websiteWithUrl = {...website, url: normalizeUrl(website.url)};
      
      // Debug: Check if firstName is preserved
      console.log(`Analyzing website ${websiteWithUrl.url}:`, {
        firstName: websiteWithUrl.firstName,
        lastName: websiteWithUrl.lastName,
        email: websiteWithUrl.email,
        company: websiteWithUrl.company
      });
      
      // Ensure we have a company name or extract from domain
      if (!websiteWithUrl.company) {
        websiteWithUrl.company = extractDomain(websiteWithUrl.url);
      }
      
      // Initialize data containers
      let websiteData = {};
      let linkedInData = {};
      let talkingPointsData = {};
      
      // Track scraping success for each enabled scraper
      let scrapingResults = {
        website: { enabled: scrapingOptions.scrapeWebsite, success: false },
        linkedin: { enabled: scrapingOptions.scrapeLinkedIn && websiteWithUrl.linkedinUrl && isValidLinkedInUrl(websiteWithUrl.linkedinUrl), success: false },
        talkingPoints: { enabled: scrapingOptions.scrapeTalkingPoints && websiteWithUrl.url && isValidWebsiteForTalkingPoints(websiteWithUrl.url), success: false }
      };
      
      // Scrape website content if enabled
      if (scrapingResults.website.enabled) {
        try {
          console.log(`Starting website scraping for: ${websiteWithUrl.url}`);
          websiteData = await scrapeWebsite(websiteWithUrl.url);
          
          // Check if website scraping succeeded
          if (websiteData.source !== 'fallback' || !websiteData.error) {
            scrapingResults.website.success = true;
            console.log(`✓ Successfully scraped website data for ${websiteWithUrl.url}`, {
              title: websiteData.title,
              contentLength: websiteData.content?.length || 0,
              source: websiteData.source
            });
          } else {
            console.warn(`⚠ Website scraping failed for ${websiteWithUrl.url}: ${websiteData.error}`);
          }
        } catch (error) {
          console.error(`✗ Website scraping failed for ${websiteWithUrl.url}:`, error.message);
          // Don't throw error, just mark as failed
        }
      }
      
      // Scrape LinkedIn profile if enabled and LinkedIn URL is available
      if (scrapingResults.linkedin.enabled) {
        try {
          console.log(`Starting LinkedIn scraping for: ${websiteWithUrl.linkedinUrl}`);
          const linkedinUsername = extractLinkedInUsername(websiteWithUrl.linkedinUrl);
          if (!linkedinUsername) {
            throw new Error(`Invalid LinkedIn URL format: ${websiteWithUrl.linkedinUrl}`);
          }
          
          const rawLinkedInData = await scrapeLinkedInProfile(websiteWithUrl.linkedinUrl);
          
          // Check if LinkedIn scraping succeeded
          if (rawLinkedInData.success) {
            console.log('Raw LinkedIn data received:', rawLinkedInData.data);
            
            // Process LinkedIn data for opener generation
            linkedInData = processLinkedInDataForOpeners(rawLinkedInData);
            
            console.log('Processed LinkedIn data:', {
              name: linkedInData?.name,
              headline: linkedInData?.headline,
              summary: linkedInData?.summary,
              experience: linkedInData?.experience,
              hasValidData: linkedInData && (linkedInData.headline || linkedInData.name || linkedInData.summary || linkedInData.experience)
            });
            
            if (linkedInData && (linkedInData.headline || linkedInData.name || linkedInData.summary || linkedInData.experience)) {
              scrapingResults.linkedin.success = true;
              console.log(`✓ Successfully scraped LinkedIn data for ${websiteWithUrl.linkedinUrl}`, {
                name: linkedInData.name,
                headline: linkedInData.headline?.substring(0, 50) + '...',
                company: linkedInData.company
              });
            } else {
              console.warn(`⚠ LinkedIn scraping returned no usable data for ${websiteWithUrl.linkedinUrl}`);
              console.warn('LinkedIn data validation failed:', linkedInData);
            }
          } else {
            console.warn(`⚠ LinkedIn scraping failed for ${websiteWithUrl.linkedinUrl}: ${rawLinkedInData.error}`);
          }
        } catch (error) {
          console.error(`✗ LinkedIn scraping failed for ${websiteWithUrl.linkedinUrl}:`, error.message);
          // Don't throw error, just mark as failed
        }
      }
      
      // Scrape talking points if enabled and website URL is available
      if (scrapingResults.talkingPoints.enabled) {
        try {
          console.log(`Starting talking points scraping for: ${websiteWithUrl.url}`);
          const rawTalkingPointsData = await scrapeTalkingPoints(websiteWithUrl.url);
          
          // Check if talking points scraping succeeded
          if (rawTalkingPointsData.success) {
            // Process talking points data for opener generation
            talkingPointsData = processTalkingPointsForOpeners(rawTalkingPointsData);
            
            if (talkingPointsData && talkingPointsData.websiteName) {
              scrapingResults.talkingPoints.success = true;
              console.log(`✓ Successfully scraped talking points for ${websiteWithUrl.url}`, {
                websiteName: talkingPointsData.websiteName,
                summaryLength: talkingPointsData.websiteSummary?.length || 0,
                opportunitiesCount: talkingPointsData.opportunities?.length || 0
              });
            } else {
              console.warn(`⚠ Talking points scraping returned no usable data for ${websiteWithUrl.url}`);
            }
          } else {
            console.warn(`⚠ Talking points scraping failed for ${websiteWithUrl.url}: ${rawTalkingPointsData.error}`);
          }
        } catch (error) {
          console.error(`✗ Talking points scraping failed for ${websiteWithUrl.url}:`, error.message);
          // Don't throw error, just mark as failed
        }
      }
      
      // Check if at least one enabled scraper succeeded
      const enabledScrapers = Object.values(scrapingResults).filter(result => result.enabled);
      const successfulScrapers = enabledScrapers.filter(result => result.success);
      const shouldGenerateOpener = enabledScrapers.length === 0 || successfulScrapers.length > 0;
      
      // Log comprehensive scraping summary
      console.log('\n=== SCRAPING SUMMARY ===');
      console.log(`Website: ${websiteWithUrl.url}`);
      console.log(`LinkedIn URL: ${websiteWithUrl.linkedinUrl || 'None'}`);
      console.log('Scraping Options:', scrapingOptions);
      console.log('\nScraping Results:');
      console.log(`✓ Website Data: ${scrapingResults.website.enabled ? (scrapingResults.website.success ? 'SUCCESS' : 'FAILED') : 'DISABLED'}${websiteData && websiteData.source ? ` (${websiteData.source})` : ''}`);
      console.log(`✓ LinkedIn Data: ${scrapingResults.linkedin.enabled ? (scrapingResults.linkedin.success ? 'SUCCESS' : 'FAILED') : 'DISABLED'}`);
      console.log(`✓ Talking Points: ${scrapingResults.talkingPoints.enabled ? (scrapingResults.talkingPoints.success ? 'SUCCESS' : 'FAILED') : 'DISABLED'}`);
      console.log(`\nOpener Generation: ${shouldGenerateOpener ? 'PROCEEDING' : 'SKIPPED - All enabled scrapers failed'}`);
      console.log(`Enabled scrapers: ${enabledScrapers.length}, Successful: ${successfulScrapers.length}`);
      console.log('========================\n');
      
      // Create comprehensive summary from available data
      let rawSummary = `Company: ${websiteWithUrl.company}`;
      
      // Add website data if available
      if (websiteData.title) {
        rawSummary += `\n\nWebsite Title: ${websiteData.title}`;
        rawSummary += `\n\nMeta Description: ${websiteData.metaDescription || 'N/A'}`;
        rawSummary += `\n\nHeadings: ${websiteData.headings || 'N/A'}`;
        rawSummary += `\n\nContent: ${websiteData.content ? websiteData.content.substring(0, 1000) + (websiteData.content.length > 1000 ? '...' : '') : 'N/A'}`;
      }
      
      // Add LinkedIn data if available
      if (linkedInData && (linkedInData.headline || linkedInData.name || linkedInData.summary || linkedInData.experience)) {
        rawSummary += `\n\n--- LinkedIn Profile ---`;
        if (linkedInData.name) rawSummary += `\nName: ${linkedInData.name}`;
        if (linkedInData.headline) rawSummary += `\nHeadline: ${linkedInData.headline}`;
        if (linkedInData.summary) rawSummary += `\nSummary: ${linkedInData.summary}`;
        if (linkedInData.experience) rawSummary += `\nExperience: ${typeof linkedInData.experience === 'string' ? linkedInData.experience.substring(0, 500) + (linkedInData.experience.length > 500 ? '...' : '') : JSON.stringify(linkedInData.experience).substring(0, 500)}`;
        if (linkedInData.education) rawSummary += `\nEducation: ${typeof linkedInData.education === 'string' ? linkedInData.education.substring(0, 300) + (linkedInData.education.length > 300 ? '...' : '') : JSON.stringify(linkedInData.education).substring(0, 300)}`;
        if (linkedInData.highlights) rawSummary += `\nHighlights: ${typeof linkedInData.highlights === 'string' ? linkedInData.highlights.substring(0, 300) + (linkedInData.highlights.length > 300 ? '...' : '') : JSON.stringify(linkedInData.highlights).substring(0, 300)}`;
        if (linkedInData.skills) rawSummary += `\nSkills: ${Array.isArray(linkedInData.skills) ? linkedInData.skills.join(', ') : linkedInData.skills}`;
      }
      
      // Add talking points data if available
      if (talkingPointsData && talkingPointsData.websiteName) {
        rawSummary += `\n\n--- Talking Points ---`;
        rawSummary += `\nWebsite: ${talkingPointsData.websiteName}`;
        rawSummary += `\nSummary: ${talkingPointsData.websiteSummary || 'N/A'}`;
        if (talkingPointsData.opportunities && talkingPointsData.opportunities.length > 0) {
          rawSummary += `\nOpportunities: ${talkingPointsData.opportunities.join('; ')}`;
        }
      }
      
      // Qualify prospect based on scraped data FIRST
      let qualificationResult = null;
      const scrapedDataForQualification = {
        content: websiteData.content,
        title: websiteData.title,
        metaDescription: websiteData.metaDescription,
        company: websiteWithUrl.company,
        linkedInData: linkedInData && (linkedInData.headline || linkedInData.name || linkedInData.summary || linkedInData.experience) ? linkedInData : null,
        talkingPointsData: talkingPointsData && talkingPointsData.websiteName ? talkingPointsData : null
      };
      
      if (shouldQualifyProspect(scrapedDataForQualification)) {
        try {
          console.log(`Starting prospect qualification for ${websiteWithUrl.url}...`);
          qualificationResult = await qualifyProspect(scrapedDataForQualification, serviceOptions, config, websiteWithUrl.url);
          console.log(`Qualification completed for ${websiteWithUrl.url}:`, {
            success: qualificationResult.success,
            fitScore: qualificationResult.fitScore
          });
        } catch (qualificationError) {
          console.warn(`Prospect qualification failed for ${websiteWithUrl.url}:`, qualificationError.message);
          qualificationResult = {
            success: false,
            error: qualificationError.message,
            fitScore: 0,
            reasoning: `Qualification failed: ${qualificationError.message}`
          };
        }
      } else {
        console.log(`Skipping prospect qualification for ${websiteWithUrl.url} - insufficient data`);
        qualificationResult = {
          success: false,
          error: 'Insufficient data for qualification',
          fitScore: 0,
          reasoning: 'Unable to qualify prospect - no scraped data available'
        };
      }
      
      // Generate personalized opener with qualification context
      let personalizedOpener;
      
      if (shouldGenerateOpener) {
        const combinedData = {
          ...websiteData,
          company: websiteWithUrl.company,
          summary: rawSummary,
          linkedInData: linkedInData && (linkedInData.headline || linkedInData.name || linkedInData.summary || linkedInData.experience) ? linkedInData : null,
          talkingPointsData: talkingPointsData && talkingPointsData.websiteName ? talkingPointsData : null,
          // Include qualification results for opener generation
          qualificationResult: qualificationResult
        };
        
        personalizedOpener = await generatePersonalizedOpener(
          combinedData,
          config,
          serviceOptions
        );
      } else {
        personalizedOpener = '[Opener generation skipped - All enabled scrapers failed. Please check your scraping configuration or try again later.]';
        console.log(`Skipping opener generation for ${websiteWithUrl.url} - no successful scrapers`);
      }
      
      const analysis = {
        summary: rawSummary,
        personalizedOpener
      };
      
      const finalResult = {
        ...websiteWithUrl,
        ...websiteData, // Include scraped website data (title, metaDescription, etc.)
        ...analysis,
        type: 'website_analysis',
        analyzedAt: new Date().toISOString(),
        serviceType: serviceOptions.serviceType || 'website',
        subServiceType: serviceOptions.subServiceType || null,
        linkedInData: linkedInData && (linkedInData.headline || linkedInData.name || linkedInData.summary || linkedInData.experience) ? linkedInData : null,
        talkingPointsData: talkingPointsData && talkingPointsData.websiteName ? talkingPointsData : null,
        scrapingOptions: scrapingOptions,
        scrapingResults: scrapingResults, // Include scraping status for UI display
        // Add qualification results
        fitScore: qualificationResult?.fitScore || 0,
        qualificationReasoning: qualificationResult?.reasoning || 'No qualification performed',
        qualificationSuccess: qualificationResult?.success === true, // Only true if explicitly successful
        keyIndicators: qualificationResult?.keyIndicators || [],
        potential_value: qualificationResult?.potentialValue || '',
        confidence: qualificationResult?.confidence || 'low'
      };
      
      // Debug: Check final result before pushing
      console.log(`Final result for ${websiteWithUrl.url}:`, {
        firstName: finalResult.firstName,
        lastName: finalResult.lastName,
        email: finalResult.email,
        company: finalResult.company,
        hasPersonalizedOpener: !!finalResult.personalizedOpener
      });
      
      results.push(finalResult);
      
      // Add delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 3000));
    } catch (error) {
      console.error(`Failed to analyze ${website.url}:`, error);
      
      // Add error message instead of fallback
      results.push({
        ...website,
        url: normalizeUrl(website.url),
        type: 'website_analysis',
        summary: 'Analysis failed: ' + error.message,
        personalizedOpener: '[Error generating opener. Please check your API configuration and try again.]',
        error: error.message,
        serviceType: serviceOptions.serviceType || 'website',
        subServiceType: serviceOptions.subServiceType || null,
        // Add default qualification fields for error cases
        fitScore: 0,
        qualificationReasoning: 'Analysis failed - unable to qualify prospect',
        qualificationSuccess: false,
        keyIndicators: [],
        concerns: ['Analysis failed'],
        confidence: 'low'
      });
    }
  }

  onProgress(websites.length);
  return results;
};

// Service Worker-enabled website analysis for background processing
export const analyzeWebsitesWithServiceWorker = async (websites, config, onProgress, serviceOptions = {}, scrapingOptions = { scrapeWebsite: true, scrapeLinkedIn: false, scrapeTalkingPoints: false }) => {
  // Check if service worker is available
  if (!serviceWorkerManager.isAvailable()) {
    console.log('Service worker not available, falling back to regular analysis');
    return analyzeWebsites(websites, config, onProgress, serviceOptions, scrapingOptions);
  }

  try {
    // Generate unique task ID
    const taskId = serviceWorkerManager.generateTaskId();
    
    // Register this as a background task
    pageVisibilityManager.registerBackgroundTask(taskId, 'website_analysis', {
      websites,
      config,
      serviceOptions,
      scrapingOptions
    });

    console.log(`Starting service worker website analysis for ${websites.length} websites`);
    
    // Define progress callback
    const handleProgress = (message) => {
      const { type, progress, currentWebsite, result, results, error } = message;
      
      switch (type) {
        case 'ANALYSIS_PROGRESS':
          console.log(`Analysis progress: ${progress}% - Currently analyzing: ${currentWebsite}`);
          if (onProgress) {
            onProgress(Math.floor((progress / 100) * websites.length), result);
          }
          break;
          
        case 'ANALYSIS_COMPLETE':
          console.log('Service worker analysis completed');
          pageVisibilityManager.unregisterBackgroundTask(taskId);
          if (onProgress) {
            onProgress(websites.length, null, results);
          }
          break;
          
        case 'ANALYSIS_ERROR':
          console.error('Service worker analysis failed:', error);
          pageVisibilityManager.unregisterBackgroundTask(taskId);
          throw new Error(error);
          
        default:
          console.log('Unknown analysis message:', type);
      }
    };

    // Start analysis in service worker
    const result = await serviceWorkerManager.startWebsiteAnalysis(
      websites,
      config,
      serviceOptions,
      scrapingOptions,
      handleProgress
    );

    return result.results || [];
    
  } catch (error) {
    console.error('Service worker analysis failed, falling back to regular analysis:', error);
    
    // Fallback to regular analysis
    return analyzeWebsites(websites, config, onProgress, serviceOptions, scrapingOptions);
  }
};

// Enhanced analyze function that automatically chooses between service worker and regular analysis
export const analyzeWebsitesEnhanced = async (websites, config, onProgress, serviceOptions = {}, scrapingOptions = { scrapeWebsite: true, scrapeLinkedIn: false, scrapeTalkingPoints: false }, useServiceWorker = true) => {
  // If service worker is requested and available, use it
  if (useServiceWorker && serviceWorkerManager.isAvailable()) {
    try {
      return await analyzeWebsitesWithServiceWorker(websites, config, onProgress, serviceOptions, scrapingOptions);
    } catch (error) {
      console.warn('Service worker analysis failed, falling back to regular analysis:', error);
    }
  }
  
  // Fallback to regular analysis
  return analyzeWebsites(websites, config, onProgress, serviceOptions, scrapingOptions);
};

// Check if background analysis is supported (now always true with server-side processing)
export const isBackgroundAnalysisSupported = () => {
  return true; // Server-side background processing is always available
};

// Start server-side background website analysis
export const startBackgroundWebsiteAnalysis = async (websites, config = {}, scrapingOptions = {}) => {
  try {
    const task = await backgroundTaskService.startWebsiteAnalysis(websites, {
      ...config,
      scrapingOptions
    });
    
    console.log(`Started server-side background analysis: ${task.id}`);
    return task;
  } catch (error) {
    console.error('Failed to start background analysis:', error);
    throw error;
  }
};

// Get status of background analysis tasks
export const getBackgroundAnalysisStatus = async (taskId) => {
  try {
    return await backgroundTaskService.getTask(taskId);
  } catch (error) {
    console.error('Failed to get background analysis status:', error);
    return null;
  }
};

// Get results of background analysis
export const getBackgroundAnalysisResults = async (taskId) => {
  try {
    return await backgroundTaskService.getTaskResults(taskId);
  } catch (error) {
    console.error('Failed to get background analysis results:', error);
    return null;
  }
};

// Stop background analysis (cancel if pending)
export const stopBackgroundAnalysis = async (taskId) => {
  try {
    await backgroundTaskService.cancelTask(taskId);
    console.log(`Cancelled background analysis: ${taskId}`);
  } catch (error) {
    console.error('Failed to stop background analysis:', error);
  }
};

// Get all user's background analysis tasks
export const getActiveBackgroundAnalysisTasks = async () => {
  try {
    return await backgroundTaskService.getUserTasks(null, 'website_analysis');
  } catch (error) {
    console.error('Failed to get active background analysis tasks:', error);
    return [];
  }
};

// Poll task until completion with progress updates
export const pollBackgroundAnalysisUntilComplete = async (taskId, onProgress = null) => {
  try {
    return await backgroundTaskService.pollTaskUntilComplete(taskId, onProgress);
  } catch (error) {
    console.error('Background analysis failed:', error);
    throw error;
  }
};

// Subscribe to real-time task updates
export const subscribeToBackgroundAnalysisUpdates = (taskId, callback) => {
  return backgroundTaskService.subscribeToTaskUpdates(taskId, callback);
};